/**
 * UnitLayoutPreview - Visual 2D preview of unit layout
 *
 * Shows units as proportional rectangles with widths displayed.
 */

import { useMemo } from 'react'
import { cn } from '@/lib/utils'

interface UnitLayoutPreviewProps {
  unitWidths: number[]
  buildingWidth: number
  wallThickness: number
  className?: string
}

export function UnitLayoutPreview({
  unitWidths,
  buildingWidth,
  wallThickness,
  className,
}: UnitLayoutPreviewProps) {
  // Calculate proportional widths for display
  const unitDisplayData = useMemo(() => {
    return unitWidths.map((width, index) => ({
      index,
      width,
      // Percentage of total building length
      percentage: (width / buildingWidth) * 100,
      // Whether this unit is narrower than 15ft (minimum)
      isTooNarrow: width < 15,
    }))
  }, [unitWidths, buildingWidth, wallThickness])

  // Calculate wall width percentage
  const wallPercentage = (wallThickness / buildingWidth) * 100

  return (
    <div className={cn('rounded-md border overflow-hidden', className)}>
      {/* Building container */}
      <div className="flex h-16 bg-muted/30">
        {/* Left exterior wall */}
        <div
          className="bg-slate-400 dark:bg-slate-600 shrink-0"
          style={{ width: `${wallPercentage}%`, minWidth: '2px' }}
        />

        {/* Units and partition walls */}
        {unitDisplayData.map((unit, index) => (
          <div key={index} className="contents">
            {/* Unit */}
            <div
              className={cn(
                'flex items-center justify-center text-xs font-medium transition-colors',
                unit.isTooNarrow
                  ? 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300'
                  : 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-300'
              )}
              style={{ width: `${unit.percentage}%`, minWidth: '20px' }}
              title={`Unit ${unit.index + 1}: ${unit.width.toFixed(1)} ft`}
            >
              <span className="truncate px-1">
                {unit.width >= 30 ? `${unit.width.toFixed(0)}′` : unit.index + 1}
              </span>
            </div>

            {/* Partition wall (except after last unit) */}
            {index < unitWidths.length - 1 && (
              <div
                className="bg-slate-300 dark:bg-slate-700 shrink-0"
                style={{ width: `${wallPercentage}%`, minWidth: '1px' }}
              />
            )}
          </div>
        ))}

        {/* Right exterior wall */}
        <div
          className="bg-slate-400 dark:bg-slate-600 shrink-0"
          style={{ width: `${wallPercentage}%`, minWidth: '2px' }}
        />
      </div>

      {/* Legend */}
      <div className="px-2 py-1 text-xs text-muted-foreground border-t flex justify-between bg-muted/10">
        <span>{unitWidths.length} units</span>
        <span>{buildingWidth} ft total</span>
      </div>
    </div>
  )
}
